<template>
	<validation-observer
		tag="div"
		class="cassie-vertical-md"
	>
		<QuestionGroup>
			<QuestionCard
				title="How should this service identify successful responses?"
				description="Specify the HTTP response codes and, if needed, part of a response message that signals a successful data transfer. For example, entering 'Success' will match any message containing that word."
			>
				<div class="d-flex flex-row cassie-horizontal-sm response-inputs-row">
					<TextField
						v-model="expectedSuccessResponseCode"
						type="number"
						label="Success HTTP Response Code *"
						class="cassie-input-width-sm"
						:disabled="!userFullPermissions"
					/>
					<TextField
						v-model="expectedSuccessResponseMessage"
						label="Success HTTP Response Message"
						class="cassie-input-width-sm"
						:disabled="!userFullPermissions"
					/>
					<PrimaryActionButton
						v-if="userFullPermissions"
						:disabled="!expectedSuccessResponseCode"
						@click="addSuccessResponse"
					>
						Add
					</PrimaryActionButton>
				</div>

				<v-simple-table
					v-if="successResponses && successResponses.length > 0"
					dense
					light
					class="success-responses-table"
				>
					<template #default>
						<thead>
							<tr>
								<th
									class="text-left"
									scope="col"
									style="width: 50%"
								>
									Response Code
								</th>
								<th
									class="text-left"
									scope="col"
									style="width: 50%"
								>
									Response Message
								</th>
								<th
									v-if="userFullPermissions"
									class="text-center"
									scope="col"
									style="width: 80px"
								>
									Action
								</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="(message, index) in successResponses"
								:key="index"
							>
								<td class="text-left">
									{{ displayResponseCode(index) }}
								</td>
								<td class="text-left">
									{{ displayResponseMessage(index) }}
								</td>
								<td
									v-if="userFullPermissions"
									class="text-center"
								>
									<v-menu
										bottom
										left
										offset-y
									>
										<template #activator="{ on, attrs }">
											<v-btn
												icon
												small
												v-bind="attrs"
												v-on="on"
												@click.stop
											>
												<v-icon small>
													mdi-dots-horizontal
												</v-icon>
											</v-btn>
										</template>
										<v-list dense>
											<v-list-item @click="removeSuccessResponses(index)">
												<v-list-item-content>
													<v-list-item-title>
														Remove
													</v-list-item-title>
												</v-list-item-content>
											</v-list-item>
										</v-list>
									</v-menu>
								</td>
							</tr>
						</tbody>
					</template>
				</v-simple-table>
			</QuestionCard>

			<!-- Question 2: Update Cassie Contacts with Returned Value Mappings -->
			<p
				v-if="isGetMethod"
				class="step-section-description"
			>
				GET services retrieve data. Map at least one returned value below to store what comes back against the contact.
			</p>
			<ReturnedValueMappingsTable
				:returned-value-mappings="returnedValueMappings"
				:returned-value="returnedValue"
				:object-client-urn-column="objectClientUrnColumn"
				:user-full-permissions="userFullPermissions"
				:cassie-identifier-fields="cassieIdentifierFields"
				:show-update-enabled-panel="showUpdateEnabledPanel"
				:update-cassie-contacts="localUpdateCassieContacts"
				@update:mappingsBatch="$emit('update:mappingsBatch', $event)"
				@update:updateCassieContacts="setUpdateCassieContacts"
			/>
		</QuestionGroup>
	</validation-observer>
</template>
<script>
import { mapGetters } from 'vuex'
import TextField from '../../../../../../shared/components/text-field.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import { QuestionCard, QuestionGroup } from '../../../../components/component-library-wrapper.js'
import ReturnedValueMappingsTable from './ReturnedValueMappingsTable.vue'

export default {
	components: { TextField, PrimaryActionButton, QuestionCard, QuestionGroup, ReturnedValueMappingsTable },
	props: {
		httpMethod: {
			type: String,
			default: null
		},
		successResponses: {
			type: Array,
			default: () => []
		},
		userFullPermissions: Boolean,
		isEdit: {
			type: Boolean,
			default: false
		},
		dataTypeItems: {
			type: Array,
			default: () => []
		},
		cassieObjectId: {
			type: Number,
			default: null
		},
		selectedFields: {
			type: Array,
			default: () => []
		},
		objectClientUrnColumn: {
			type: String,
			default: null
		},
		returnedValue: {
			type: String,
			default: null
		},
		returnedValueMappings: {
			type: Array,
			default: () => []
		},
		cassieObjects: {
			type: Array,
			default: () => []
		},
		updateCassieContacts: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			expectedSuccessResponseCode: null,
			expectedSuccessResponseMessage: null,
			localUpdateCassieContacts: this.updateCassieContacts
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission', 'userHasUniversalBrand']),
		isGetMethod () {
			return (this.httpMethod || '').toUpperCase() === 'GET'
		},
		showUpdateEnabledPanel () {
			const isUpdateStanddak = this.dataTypeItems.find(({ value }) => value === this.cassieObjectId)?.isUpdateStanddak
			return isUpdateStanddak
		},
		cassieIdentifierFields () {
			const foundCassieObjects = this.cassieObjects.find(({ cassieObjectId }) => cassieObjectId === this.cassieObjectId)?.cassieObjectFields
			let foundItems = foundCassieObjects?.map(field => ({
				value: field.cassieObjectFieldName,
				text: field.cassieObjectFieldName
			}))

			foundItems = foundItems?.filter(({ value }) => value.toLowerCase().startsWith('urn') || value.toLowerCase().startsWith('custom'))
			return foundItems
		}
	},
	watch: {
		updateCassieContacts: {
			handler (newVal) {
				this.localUpdateCassieContacts = newVal
			},
			immediate: true
		},
		objectClientUrnColumn: {
			handler (newValue) {
				// Auto-enable updateCassieContacts if either field is populated
				if (newValue && this.showUpdateEnabledPanel && !this.isEdit && !this.localUpdateCassieContacts) {
					this.setUpdateCassieContacts(true)
				}
			},
			immediate: true
		},
		returnedValue: {
			handler (newValue) {
				// Auto-enable updateCassieContacts if either field is populated
				if (newValue && this.showUpdateEnabledPanel && !this.isEdit && !this.localUpdateCassieContacts) {
					this.setUpdateCassieContacts(true)
				}
			},
			immediate: true
		}
	},
	methods: {
		updateSuccessResponses (successResponses) {
			this.$emit('update:successResponses', successResponses)
		},
		removeSuccessResponses (index) {
			const successResponses = [...this.successResponses]
			this.$delete(successResponses, index)
			this.updateSuccessResponses(successResponses)
		},
		addSuccessResponse () {
			this.updateSuccessResponses([
				...this.successResponses,
				{
					expectedSuccessResponseCode: this.expectedSuccessResponseCode,
					expectedSuccessResponseMessage: this.expectedSuccessResponseMessage
				}
			])
			this.expectedSuccessResponseCode = null
			this.expectedSuccessResponseMessage = null
		},
		displayResponseCode (index) {
			return this.successResponses[index]?.expectedSuccessResponseCode
		},
		displayResponseMessage (index) {
			return this.successResponses[index]?.expectedSuccessResponseMessage
		},
		setUpdateCassieContacts (value) {
			this.localUpdateCassieContacts = value
			// Emit the value to parent component if needed
			this.$emit('update:updateCassieContacts', value)
		}
	}
}
</script>

<style lang="scss" scoped>
.step-description {
	margin-bottom: 24px;
}

.step-description-text {
	margin: 0;
	color: var(--global-muted-foreground);
	font-size: 14px;
	line-height: 1.5;
}

.response-inputs-row {
  width: calc(65% + 8px);

  .cassie-input-width-sm {
    width: auto !important;
    flex: 1;
  }
}

.success-responses-table {
	border: 1px solid var(--global-border-color);
	border-radius: var(--radius, 8px);
	margin-top: 16px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	overflow: hidden;
	width: calc(65% + 8px) !important;

	:deep(.v-data-table__wrapper) {
		border-radius: var(--radius, 8px);
	}
}

</style>
